import { useCallback, useEffect, useState } from 'react';

interface PaginationState {
    pageNo: number;
    pageSize: number;
}

interface UsePaginationReturn {
    pagination: PaginationState;
    handleChangePage: (event: unknown, newPage: number) => void;
    handleChangeRowsPerPage: (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => void;
    setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
    activeFilterCard: any;
    setActiveFilterCard: React.Dispatch<React.SetStateAction<any>>;
    filterValues: any;
    setFilterValues: React.Dispatch<React.SetStateAction<any>>;
    searchTextValue: string;
    setSearchTextValue: React.Dispatch<React.SetStateAction<string>>;
    payload: any;
    setPayload: React.Dispatch<React.SetStateAction<any>>;
    onFilterApplied: (data?: any) => void;
    onBackendSearch: (searchText: any) => void;
    onSearchTextChange: (searchText: any) => void;
    onRemoveAll: () => void;
    handleActiveFilterCard: (card: any) => void;
    clearSearch: () => void;
    onManualSearch: (searchText: any) => void;
    onManualSearchClear: () => void;
}

const usePagination = (
    initialPage: number = 0,
    initialPageSize: number = 25,
    initialActiveFilterCard: any = {},
    initialFilterValues: any = {},
    initialSearchText: string = '',
    initialPayload: any = {},
    disableAutoSearch: boolean = false,
): UsePaginationReturn => {
    const [activeFilterCard, setActiveFilterCard] = useState<any>(
        initialActiveFilterCard,
    );
    const [firstRenderDone, setFirstRenderDone] = useState(false);

    const [filterValues, setFilterValues] = useState<any>(initialFilterValues);
    const [searchTextValue, setSearchTextValue] =
        useState<string>(initialSearchText);
    const [payload, setPayload] = useState<any>(initialPayload);
    const [pagination, setPagination] = useState<PaginationState>({
        pageNo: initialPage,
        pageSize: initialPageSize,
    });

    const handleChangePage = (event: unknown, newPage: number) => {
        setPagination((prev: any) => {
            return {
                ...prev,
                pageNo: newPage,
            };
        });
        setPayload((prev: any) => {
            return {
                ...prev,
                pageNo: newPage,
            };
        });
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPagination((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
                pageSize: +event.target.value,
            };
        });
        setPayload((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
                pageSize: event.target.value,
            };
        });
    };

    const handleActiveFilterCard = (card: any) => {
        setActiveFilterCard(card);
        setPayload((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
                searchText: searchTextValue,
                activeFilterCard: card,
            };
        });
        setPagination((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
            };
        });
    };

    useEffect(() => {
        if (!!firstRenderDone && !disableAutoSearch) {
            setPayload((prev: any) => {
                return {
                    ...prev,
                    pageNo: 0,
                    searchText: searchTextValue,
                };
            });
            setPagination((prev: any) => {
                return {
                    ...prev,
                    pageNo: 0,
                };
            });
        }
    }, [searchTextValue, firstRenderDone, disableAutoSearch]);

    const onFilterApplied = (data?: any) => {
        setPayload((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
                ...filterValues,
                ...data,
            };
        });
        setPagination((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
            };
        });
    };

    const onBackendSearch = (searchText: any) => {
        setPagination((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
            };
        });
        setSearchTextValue(searchText);
    };
    const onManualSearch = useCallback(() => {
        setPayload((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
                searchText: searchTextValue,
            };
        });
        setPagination((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
            };
        });
    }, [searchTextValue]);
    const onManualSearchClear = () => {
        setPayload((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
                searchText: '',
            };
        });
        setPagination((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
            };
        });
    };

    const onSearchTextChange = (searchText: any) => {
        if ((!searchText || searchText === '') && disableAutoSearch) {
            onManualSearchClear();
        }
        setSearchTextValue(searchText);
    };

    const onRemoveAll = () => {
        setFilterValues(initialFilterValues);
        setPayload({
            initialPayload,
        });
        setPagination((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
            };
        });
    };

    const clearSearch = () => {
        setSearchTextValue('');
        setPagination((prev: any) => {
            return {
                ...prev,
                pageNo: 0,
            };
        });
    };

    useEffect(() => {
        setFirstRenderDone(true);
    }, []);
    return {
        pagination,
        handleChangePage,
        handleChangeRowsPerPage,
        setPagination,
        activeFilterCard,
        setActiveFilterCard,
        filterValues,
        setFilterValues,
        searchTextValue,
        setSearchTextValue,
        payload,
        setPayload,
        onFilterApplied,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        handleActiveFilterCard,
        clearSearch,
        onManualSearch,
        onManualSearchClear,
    };
};

export default usePagination;
